import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const CATEGORIES = [
  {
    id: 'installation',
    label: 'Installation & Upgrades',
    shortLabel: 'Installation',
    icon: 'ri-tools-fill',
    services: [
      { icon: 'ri-flashlight-fill', title: 'Electrical Installations', desc: 'New circuits, sockets and full electrical installations to 18th Edition wiring regulations.' },
      { icon: 'ri-home-fill', title: 'Full & Partial Rewiring', desc: 'Complete domestic rewiring carried out with minimal disruption, fully certified and documented.' },
      { icon: 'ri-dashboard-fill', title: 'Consumer Unit Upgrades', desc: 'Modern RCBO consumer units for maximum circuit protection, bringing your home up to current standards.' },
      { icon: 'ri-lightbulb-flash-fill', title: 'Lighting Installation', desc: 'Bespoke lighting solutions for any room — spotlights, pendants, strips and more.' },
      { icon: 'ri-sun-line', title: 'LED Lighting Upgrades', desc: 'Switch to energy-saving LED throughout your home or business. Huge savings on your bills.' },
      { icon: 'ri-seedling-fill', title: 'Outdoor & Garden Lighting', desc: 'Landscape, pathway and feature lighting to transform your outdoor spaces.' },
      { icon: 'ri-search-eye-fill', title: 'Security Lighting', desc: 'PIR motion sensor and floodlight installations to deter intruders and keep your property safe.' },
      { icon: 'ri-plug-fill', title: 'Socket & Switch Installation', desc: 'Additional sockets, USB outlets, smart switches and more — fitted neatly and cleanly.' },
      { icon: 'ri-car-fill', title: 'EV Charger Installation', desc: 'OLEV approved home EV charger installations. Fast, reliable and government grant eligible.' },
      { icon: 'ri-home-wifi-fill', title: 'Smart Home Installations', desc: 'Smart switches, automated lighting and connected home systems wired and configured.' },
      { icon: 'ri-robot-fill', title: 'Home Automation Systems', desc: 'Full home automation from app-controlled lighting to motorised blinds and smart security.' },
      { icon: 'ri-temp-hot-fill', title: 'Heating Controls & Thermostats', desc: 'Smart thermostat installation and heating control wiring for maximum comfort and efficiency.' },
      { icon: 'ri-water-flash-fill', title: 'Electric Shower Installation', desc: 'Zone-compliant electric shower installation with full earthing, bonding and certification.' },
      { icon: 'ri-restaurant-fill', title: 'Kitchen Electrical Installations', desc: 'Cooker circuits, extractor hoods, under-cabinet lighting and full kitchen electrical fit-outs.' },
    ],
  },
  {
    id: 'repair',
    label: 'Repair & Maintenance',
    shortLabel: 'Repairs',
    icon: 'ri-settings-fill',
    services: [
      { icon: 'ri-wrench-fill', title: 'Electrical Repairs', desc: 'Fast, reliable repairs for all domestic and commercial electrical faults and failures.' },
      { icon: 'ri-bug-fill', title: 'Fault Finding & Diagnostics', desc: 'Accurate fault diagnosis using professional equipment — we find it fast and fix it right.' },
      { icon: 'ri-flashlight-line', title: 'Tripping Circuits', desc: 'Circuit breaker keeps tripping? We identify the root cause and resolve it safely.' },
      { icon: 'ri-inbox-fill', title: 'Fuse Box Repairs', desc: 'Safe repair of damaged or overloaded fuse boxes and consumer units to restore power.' },
      { icon: 'ri-lightbulb-line', title: 'Lighting Repairs', desc: 'Flickering lights, dead circuits or faulty fittings — all lighting faults diagnosed and fixed.' },
      { icon: 'ri-toggle-fill', title: 'Socket & Switch Repairs', desc: 'Damaged, sparking or dead sockets and switches replaced safely and neatly.' },
      { icon: 'ri-alarm-warning-fill', title: 'Emergency Call-Outs', desc: '24/7 emergency electrical response for urgent faults, power loss and dangerous situations.' },
      { icon: 'ri-file-list-3-fill', title: 'Electrical Maintenance Contracts', desc: 'Scheduled maintenance contracts for landlords and businesses. Peace of mind all year round.' },
      { icon: 'ri-cpu-fill', title: 'Electrical Appliance Repairs', desc: 'Diagnosis and repair of electrical appliances and equipment by a qualified electrician.' },
    ],
  },
  {
    id: 'safety',
    label: 'Safety & Compliance',
    shortLabel: 'Safety',
    icon: 'ri-shield-check-fill',
    services: [
      { icon: 'ri-search-eye-fill', title: 'EICR Testing', desc: 'Electrical Installation Condition Reports for landlords, homebuyers and commercial properties.' },
      { icon: 'ri-file-certificate-fill', title: 'Landlord Safety Certificates', desc: 'All landlord electrical certificates provided promptly, fully compliant with latest legislation.' },
      { icon: 'ri-home-heart-fill', title: 'Homebuyer Electrical Reports', desc: 'Pre-purchase electrical inspections to give you confidence before buying a property.' },
      { icon: 'ri-alarm-fill', title: 'Smoke & Heat Alarm Installation', desc: 'Mains-wired interlinked smoke and heat alarms installed throughout your home or business.' },
      { icon: 'ri-fire-fill', title: 'Fire Alarm Systems', desc: 'Commercial and domestic fire alarm system installation, testing and ongoing maintenance.' },
      { icon: 'ri-thunderstorms-fill', title: 'Surge Protection', desc: 'SPD surge protection devices installed to safeguard your appliances and electrical systems.' },
      { icon: 'ri-anchor-fill', title: 'Earthing & Bonding', desc: 'Supplementary bonding and earthing inspections and installations to meet current regulations.' },
      { icon: 'ri-checkbox-circle-fill', title: 'RCD Testing', desc: 'Residual Current Device testing and inspection ensuring your life-saving protection is working.' },
    ],
  },
];

// Desktop full card
function DesktopCard({ icon, title, desc, delay }: { icon: string; title: string; desc: string; delay: number }) {
  const { ref, isVisible } = useScrollAnimation(0.05);
  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className={`group bg-white rounded-2xl p-5 border border-[#E0C78A]/20 hover:border-[#C8A96A]/60 transition-all duration-500 cursor-default ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="w-11 h-11 flex items-center justify-center bg-gradient-to-br from-[#C8A96A]/15 to-[#A8894F]/10 rounded-xl mb-3 group-hover:from-[#C8A96A]/30 transition-all duration-300">
        <i className={`${icon} text-[#C8A96A] text-lg`} />
      </div>
      <h3 className="font-bold text-[#0A0A0A] text-sm mb-1 font-playfair leading-snug">{title}</h3>
      <p className="text-gray-500 text-xs leading-relaxed">{desc}</p>
    </div>
  );
}

// Mobile compact row item
function MobileItem({ icon, title, delay }: { icon: string; title: string; delay: number }) {
  const { ref, isVisible } = useScrollAnimation(0.05);
  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className={`flex items-center gap-3 bg-white rounded-xl px-3 py-3 border border-[#E0C78A]/20 active:border-[#C8A96A]/60 transition-all duration-400 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="w-8 h-8 flex items-center justify-center bg-gradient-to-br from-[#C8A96A]/15 to-[#A8894F]/10 rounded-lg flex-shrink-0">
        <i className={`${icon} text-[#C8A96A] text-sm`} />
      </div>
      <span className="text-[#0A0A0A] text-sm font-medium leading-tight">{title}</span>
    </div>
  );
}

const SERVICE_PAGE_LINKS: Record<string, string> = {
  installation: '/services/installation-upgrades',
};

export default function Services() {
  const [activeTab, setActiveTab] = useState('installation');
  const { ref: headRef, isVisible: headVisible } = useScrollAnimation();
  const activeCategory = CATEGORIES.find((c) => c.id === activeTab) ?? CATEGORIES[0];
  const navigate = useNavigate();
  const activePageLink = SERVICE_PAGE_LINKS[activeTab];

  return (
    <section id="services" className="relative overflow-hidden">
      {/* TOP GOLD BANNER */}
      <div className="bg-gradient-to-r from-[#A8894F] via-[#C8A96A] to-[#A8894F] py-3 md:py-4 px-4 md:px-6">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-black font-semibold text-xs md:text-base text-center sm:text-left">
            <i className="ri-shield-check-fill mr-1" />
            NAPIT Registered · Fully Insured · All Work Certified
          </p>
          <a
            href="tel:01529265060"
            className="bg-black/20 hover:bg-black/30 text-black font-bold text-xs md:text-sm px-5 py-2 rounded-full transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-phone-fill mr-1" />
            01529 265060
          </a>
        </div>
      </div>

      {/* MAIN SERVICES SECTION */}
      <div className="py-10 md:py-20 bg-[#F9F5EE] relative">
        <div
          className="absolute inset-0 opacity-5 pointer-events-none"
          style={{ backgroundImage: 'radial-gradient(circle, #C8A96A 1px, transparent 1px)', backgroundSize: '40px 40px' }}
        />

        <div className="max-w-7xl mx-auto px-4 md:px-6 relative">
          {/* Header */}
          <div
            ref={headRef as React.RefObject<HTMLDivElement>}
            className={`text-center mb-6 md:mb-12 transition-all duration-700 ${headVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <p className="text-[#A8894F] text-xs tracking-widest uppercase font-medium mb-2">What We Do</p>
            <h2 className="font-playfair text-3xl md:text-5xl font-bold text-[#0A0A0A] leading-tight">
              Electrical{' '}
              <span className="bg-gradient-to-r from-[#C8A96A] to-[#A8894F] bg-clip-text text-transparent">
                Services
              </span>
            </h2>
            <p className="text-gray-600 mt-2 max-w-xl mx-auto text-xs md:text-base hidden md:block">
              From simple repairs to full rewires — every job treated with care by your local Sleaford electrician.
            </p>
          </div>

          {/* ── TABS: horizontal scroll on mobile, row on desktop ── */}
          <div className="flex flex-row overflow-x-auto gap-2 pb-2 mb-6 md:mb-8 md:justify-center scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveTab(cat.id)}
                className={`flex items-center gap-1.5 px-4 py-2.5 rounded-full font-semibold text-xs md:text-sm transition-all duration-300 cursor-pointer flex-shrink-0 whitespace-nowrap ${
                  activeTab === cat.id
                    ? 'bg-gradient-to-r from-[#C8A96A] to-[#A8894F] text-black'
                    : 'bg-white border border-[#C8A96A]/30 text-[#A8894F] hover:border-[#C8A96A]'
                }`}
              >
                <i className={`${cat.icon} text-sm`} />
                {/* Short label on mobile, full on desktop */}
                <span className="md:hidden">{cat.shortLabel}</span>
                <span className="hidden md:inline">{cat.label}</span>
              </button>
            ))}
          </div>

          {/* Category strip */}
          <div className="mb-4 md:mb-6 flex items-center gap-3 flex-wrap">
            <div className="w-7 h-7 flex items-center justify-center bg-gradient-to-br from-[#C8A96A] to-[#A8894F] rounded-lg flex-shrink-0">
              <i className={`${activeCategory.icon} text-black text-xs`} />
            </div>
            <h3 className="font-playfair text-base md:text-2xl font-bold text-[#0A0A0A]">{activeCategory.label}</h3>
            <div className="flex-1 h-px bg-gradient-to-r from-[#C8A96A]/40 to-transparent hidden sm:block" />
            {activePageLink && (
              <button
                onClick={() => navigate(activePageLink)}
                className="flex items-center gap-1 text-[#A8894F] hover:text-[#C8A96A] text-xs font-semibold transition-colors duration-200 cursor-pointer whitespace-nowrap group"
              >
                Full Service Page
                <i className="ri-arrow-right-line group-hover:translate-x-0.5 transition-transform duration-200" />
              </button>
            )}
            <span className="text-[#A8894F] text-xs font-medium flex-shrink-0">{activeCategory.services.length}</span>
          </div>

          {/* ── MOBILE: compact 2-col list ── */}
          <div className="grid grid-cols-2 gap-2 md:hidden">
            {activeCategory.services.map(({ icon, title }, i) => (
              <MobileItem key={title} icon={icon} title={title} delay={i * 30} />
            ))}
          </div>

          {/* ── DESKTOP: full cards ── */}
          <div className="hidden md:grid md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {activeCategory.services.map(({ icon, title, desc }, i) => (
              <DesktopCard key={title} icon={icon} title={title} desc={desc} delay={i * 40} />
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="text-center mt-8 md:mt-12">
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="tel:01529265060"
                className="flex items-center justify-center gap-2 bg-gradient-to-r from-[#C8A96A] to-[#A8894F] hover:from-[#E0C78A] hover:to-[#C8A96A] text-black font-bold px-6 py-3.5 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-phone-fill" />
                Call Us Free
              </a>
              <button
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                className="flex items-center justify-center gap-2 border-2 border-[#C8A96A] text-[#A8894F] hover:bg-[#C8A96A]/10 font-bold px-6 py-3.5 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-file-list-3-line" />
                Free Quote
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* BOTTOM GOLD STRIP */}
      <div className="bg-[#071120] py-6 md:py-10 px-4 md:px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-4 gap-3 md:gap-6 text-center">
          {[
            { icon: 'ri-shield-check-fill', label: 'NAPIT' },
            { icon: 'ri-award-fill', label: 'Part P' },
            { icon: 'ri-time-fill', label: 'Emergency' },
            { icon: 'ri-thumb-up-fill', label: '5 Star' },
          ].map(({ icon, label }) => (
            <div key={label} className="flex flex-col items-center gap-1.5">
              <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full border border-[#C8A96A]/30 bg-[#C8A96A]/5">
                <i className={`${icon} text-[#C8A96A] text-base md:text-xl`} />
              </div>
              <span className="text-white/70 text-xs font-medium">{label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
